import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  CalendarDays,
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileCheck2,
  FileClock,
  FileDown,
  FileText,
  Link2Off,
  LucideAngularModule,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-angular';
import { Charge, Contract, Loa, LoaBillConfig, LoaSchedule, Vendor } from '../../../core/models/domain.models';
import { ApiService } from '../../../core/services/api.service';
import { AuthService } from '../../../core/services/auth.service';
import { ToastService } from '../../../shared/ui/toast.service';
import { LoaService } from '../../loa/services/loa.service';
import { ChargesService } from '../../charges/services/charges.service';
import { ContractsService, type ContractAnalysisReportRow } from '../services/contracts.service';
import { VendorsService } from '../../vendor/services/vendors.service';
import {
  buildContractChargeBreakdown,
  type ContractChargePartyLike,
} from '../utils/contract-profit.util';

const PAGE_SIZES = [10, 20, 30, 50];
const MW_PER_CONTRACT = 700;

interface ApplicationChargeApi {
  group_name: string;
  section_heading?: string | null;
  sub_group_name: string;
  rate_mwh?: number | null;
  quantum_mwh?: number | null;
  amount?: number | null;
}

interface ApplicationApi {
  id: string;
  contract_id: string;
  application_number?: string | null;
  seller_loa_id?: string | null;
  buyer_loa_id?: string | null;
  approval_charges?: ApplicationChargeApi[];
}

interface OaChargeAllocation {
  charge_type: string;
  payer: 'Buyer' | 'Seller' | 'Shared';
  buyer_percent: number;
  seller_percent: number;
}

interface EstimatedProfitChargeRow {
  applicationNumber: string;
  chargeType: string;
  rateType?: string | null;
  totalAmount: number;
  buyerAmount: number;
  sellerAmount: number;
  seciAmount: number;
  allocationLabel: string;
}

interface EstimatedProfitSummary {
  revenue: number;
  cost: number;
  applicationCharges: number;
  netProfit: number | null;
  chargeRows: EstimatedProfitChargeRow[];
}

interface EstimatedProfitModalData {
  title: string;
  subtitle: string;
  equation: string;
  hint: string;
  summaryLine: string;
  details: Array<{ label: string; value: string }>;
}

type ContractLifecycleStatus = 'active' | 'expired' | 'starts_next_month' | 'upcoming' | 'unknown';
type ContractCategoryFilter = 'all' | 'SALE-PURCHASE' | 'BANKING' | 'HYBRID';
type SummaryCategoryKey = 'sale' | 'bank' | 'hybrid' | 'bess';
type ContractPartyDetail = NonNullable<Contract['party_details']>[number];

@Component({
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './contract-list.page.html',
  styles: [
    `
      .contract-shell {
        background: linear-gradient(180deg, #ffffff 0%, #f6f9ff 100%);
        border-radius: 20px;
        border: 1px solid #dbe7ff;
        box-shadow: 0 10px 30px rgba(33, 76, 170, 0.08);
        font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
      }
      .contract-header {
        padding-bottom: 4px;
      }
      .contract-title {
        font-size: 2rem;
        font-weight: 700;
        letter-spacing: -0.03em;
        color: #24334f;
      }
      .title-badge {
        width: 42px;
        height: 42px;
        border-radius: 14px;
        background: linear-gradient(135deg, #eef4ff 0%, #dbe8ff 100%);
        color: #3d66da;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        box-shadow: inset 0 0 0 1px rgba(61, 102, 218, 0.14);
      }
      .metric-card {
        border: 1px solid #d7e4ff;
        border-top: 3px solid #4b6cff;
        border-radius: 14px;
        background: #ffffff;
        box-shadow: 0 8px 18px rgba(47, 88, 177, 0.1);
        padding: 0.8rem 1rem 0.75rem;
      }
      .metric-head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
      }
      .metric-title-wrap {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
      }
      .metric-icon-box {
        width: 28px;
        height: 28px;
        border: 1px solid #cfdcff;
        border-radius: 8px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: #6a7ff4;
        background: #f7f9ff;
      }
      .metric-label {
        color: #2b2f38;
        font-size: 14px;
        font-weight: 500;
      }
      .metric-count {
        color: #232323;
        font-size: 22px;
        font-weight: 700;
        line-height: 1;
      }
      .metric-sep {
        border-top: 1px solid #e9edf6;
        margin: 8px 0 10px;
      }
      .metric-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        font-size: 13px;
        margin-bottom: 6px;
      }
      .metric-row.clickable {
        cursor: pointer;
      }
      .metric-row.clickable:hover .metric-category,
      .metric-row.clickable:hover .metric-mw {
        opacity: 0.88;
      }
      .metric-category {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        font-weight: 600;
      }
      .metric-category.active-filter {
        text-decoration: underline;
        text-underline-offset: 3px;
      }
      .metric-category.sale {
        color: #ff4d4f;
      }
      .metric-category.bank {
        color: #3d7cff;
      }
      .metric-category.hybrid {
        color: #9064ff;
      }
      .metric-category.bess {
        color: #0f766e;
      }
      .metric-badge {
        min-width: 22px;
        height: 20px;
        padding: 0 7px;
        border-radius: 7px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: #f4f5fb;
        font-size: 12px;
        font-weight: 700;
      }
      .metric-category.sale .metric-badge {
        background: #ffe8ea;
      }
      .metric-category.bank .metric-badge {
        background: #e8f1ff;
      }
      .metric-category.hybrid .metric-badge {
        background: #efe9ff;
      }
      .metric-category.bess .metric-badge {
        background: #dff7f4;
      }
      .metric-mw {
        color: #232323;
        font-size: 13px;
        font-weight: 600;
      }
      .content-card {
        background: #ffffff;
        border: 1px solid #dbe7ff;
        border-radius: 20px;
        box-shadow: 0 10px 30px rgba(33, 76, 170, 0.08);
      }
      .tool-card {
        border: 0;
        border-radius: 0;
        background: transparent;
        padding: 0;
        box-shadow: none;
      }
      .toolbar-top {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-end;
        justify-content: space-between;
        gap: 16px;
      }
      .toolbar-actions {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .cta-btn {
        height: 40px;
        border: 1px solid #0f5fd3;
        border-radius: 999px;
        padding: 0 18px 0 12px;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        justify-content: center;
        background: linear-gradient(135deg, #0f5fd3 0%, #1e73e8 100%);
        color: #fff;
        font-size: 13px;
        font-weight: 700;
        white-space: nowrap;
        box-shadow: 0 10px 18px rgba(15, 95, 211, 0.2);
      }
      .cta-icon {
        width: 20px;
        height: 20px;
        border: 1.5px solid rgba(255, 255, 255, 0.9);
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: rgba(255, 255, 255, 0.08);
      }
      .icon-only-btn {
        width: 40px;
        height: 40px;
        border: 1px solid #d3dcef;
        border-radius: 12px;
        background: #f8fbff;
        color: #3b64db;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      .filters-grid {
        display: grid;
        grid-template-columns: repeat(5, minmax(140px, 1fr)) minmax(260px, auto);
        gap: 12px;
        align-items: end;
        flex: 1;
      }
      .field-wrap {
        position: relative;
      }
      .field-label {
        display: inline-block;
        margin-bottom: 6px;
        font-size: 12px;
        font-weight: 600;
        color: #4d5b74;
      }
      .compact-control {
        height: 40px;
        border-color: #d1dbec;
        border-radius: 12px;
        background: #ffffff;
        color: #2a3a50;
        box-shadow: none;
      }
      .compact-control::placeholder {
        color: #6a7a90;
      }
      .search-wrap {
        min-width: 200px;
      }
      .search-field-shell {
        position: relative;
      }
      .search-icon {
        position: absolute;
        top: 50%;
        right: 12px;
        transform: translateY(-50%);
        color: #8290a8;
      }
      .filter-buttons {
        display: flex;
        align-items: flex-end;
        gap: 10px;
        justify-content: flex-end;
        flex-wrap: wrap;
        min-width: 0;
      }
      .filter-btn {
        height: 40px;
        min-width: 88px;
        border-radius: 12px;
        font-size: 13px;
        font-weight: 700;
      }
      .filter-btn.go {
        border: 1px solid #0f5fd3;
        background: #0f5fd3;
        color: #fff;
      }
      .filter-btn.clear {
        border: 1px solid #d1d9e8;
        background: #fff;
        color: #7b879c;
      }
      .table-wrap {
        border: 1px solid #dfe7f5;
        border-radius: 16px;
        background: #ffffff;
        padding: 0;
        overflow: hidden;
        box-shadow: none;
      }
      .table-scroll {
        max-height: 420px;
        overflow: auto;
        border-radius: 16px;
      }
      .table-scroll::-webkit-scrollbar {
        width: 10px;
        height: 10px;
      }
      .table-scroll::-webkit-scrollbar-thumb {
        background: #8f98a6;
        border-radius: 999px;
      }
      .contract-table thead th {
        text-transform: none;
        letter-spacing: 0;
        font-size: 13px;
        border-bottom: 1px solid #d8e2f3 !important;
        background: #eef2f7;
        color: #24334f;
        font-weight: 700;
        white-space: nowrap;
      }
      .contract-table thead th.profit-col {
        background: #e9f4ff;
        color: #0f4fa8;
      }
      .contract-table tbody td {
        font-size: 14px;
        border-bottom: 1px solid #e7edf7 !important;
        background: #ffffff;
        vertical-align: middle;
      }
      .contract-table tbody td.profit-col {
        background: #f4f9ff;
      }
      .profit-value-positive {
        color: #18804b;
        font-weight: 700;
      }
      .profit-trigger {
        border: 0;
        background: transparent;
        padding: 0;
        text-decoration: underline;
        text-underline-offset: 3px;
        cursor: pointer;
      }
      .profit-value-negative {
        color: #c0392b;
        font-weight: 700;
      }
      .profit-value-neutral {
        color: #667085;
        font-weight: 600;
      }
      .contract-table tbody tr:last-child td {
        border-bottom: 0 !important;
      }
      .contract-table {
        min-width: 2050px;
        margin: 0;
      }
      .profit-modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(15, 23, 42, 0.45);
        z-index: 1040;
      }
      .profit-modal-card {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: min(760px, calc(100vw - 32px));
        max-height: calc(100vh - 48px);
        overflow: auto;
        border-radius: 18px;
        border: 1px solid #dbe7ff;
        background: #ffffff;
        box-shadow: 0 18px 40px rgba(15, 23, 42, 0.18);
        z-index: 1041;
      }
      .profit-modal-head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
        padding: 18px 20px 12px;
        border-bottom: 1px solid #e7edf7;
      }
      .profit-modal-title {
        margin: 0;
        font-size: 1.1rem;
        font-weight: 700;
        color: #24334f;
      }
      .profit-modal-subtitle {
        margin-top: 4px;
        color: #667085;
        font-size: 13px;
      }
      .profit-modal-close {
        width: 34px;
        height: 34px;
        border: 1px solid #d3dcef;
        border-radius: 10px;
        background: #fff;
        color: #3557af;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      .profit-modal-body {
        padding: 18px 20px 20px;
      }
      .profit-modal-equation {
        font-weight: 700;
        color: #24334f;
        margin-bottom: 8px;
      }
      .profit-modal-hint {
        color: #667085;
        font-size: 13px;
        margin-bottom: 14px;
      }
      .profit-modal-summary {
        color: #24334f;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.6;
        margin-bottom: 14px;
        padding: 10px 12px;
        border: 1px solid #dbe7ff;
        border-radius: 10px;
        background: #f8fbff;
      }
      .profit-modal-table td {
        vertical-align: top;
        font-size: 13px;
      }
      .badge-type {
        border: 1px solid #bfd5ff;
        color: #0e5dd8;
        background: #eef5ff;
        border-radius: 999px;
        font-size: 13px;
        padding: 4px 10px;
        font-weight: 600;
      }
      .status-pending {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 96px;
        padding: 5px 12px;
        border-radius: 999px;
        background: #fff4dd;
        color: #d98200;
        font-weight: 700;
      }
      .status-active,
      .status-expired,
      .status-starts-next-month,
      .status-upcoming,
      .status-unknown {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 126px;
        padding: 5px 12px;
        border-radius: 999px;
        font-weight: 700;
      }
      .status-active {
        background: #dcfae6;
        color: #18794e;
      }
      .status-expired {
        background: #ffebee;
        color: #d92d20;
      }
      .status-starts-next-month {
        background: #e8f1ff;
        color: #2458d3;
      }
      .status-upcoming {
        background: #fff4dd;
        color: #d98200;
      }
      .status-unknown {
        background: #eef2f7;
        color: #667085;
      }
      .pager-wrap {
        min-height: 48px;
        margin-top: 4px;
      }
      .pagination-shell {
        display: inline-flex;
        align-items: center;
        gap: 0.7rem;
        flex-wrap: wrap;
      }
      .pager-meta,
      .page-indicator {
        color: #5c6b85;
        font-size: 0.9rem;
        font-weight: 600;
      }
      .pager-btn {
        border: 1px solid #d7e0f1;
        border-radius: 999px;
        width: 30px;
        height: 30px;
        background: #f7faff;
        color: #1e4f98;
      }
      .page-indicator.current {
        min-width: 18px;
        text-align: center;
      }
      .page-size-select {
        width: 98px;
        height: 32px;
        border: 1px solid #d7e0f1;
        border-radius: 999px;
        background: #fff;
        color: #3557af;
        padding: 0 10px;
      }
      .btn.btn-primary {
        background: #0d54ba;
        border-color: #0d54ba;
        font-weight: 600;
      }
      .btn.btn-primary:hover {
        background: #0b4ca9;
        border-color: #0b4ca9;
      }
      .btn.btn-outline-secondary {
        border-color: #b9c4d2;
        color: #3d4e66;
        background: #e8edf3;
      }
      .btn-export {
        border-color: #9fcfad;
        color: #2f8f46;
        background: #e9f7ee;
      }
      .plus-ring {
        width: 18px;
        height: 18px;
        border: 2px solid #fff;
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }
      .table-action-group {
        display: flex;
        align-items: center;
        gap: 8px;
      }
      .loa-link {
        color: #3567e8;
        font-weight: 600;
        text-decoration: none;
        cursor: pointer;
      }
      .loa-link:hover {
        text-decoration: underline;
      }
      .application-count-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 34px;
        height: 28px;
        padding: 0 10px;
        border-radius: 999px;
        background: #eef4ff;
        color: #2458d3;
        font-weight: 700;
        border: 1px solid #cfe0ff;
      }
      .table-icon-btn {
        width: 30px;
        height: 30px;
        border: 1px solid #d8e2f3;
        border-radius: 8px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        background: #fff;
      }
      .table-icon-btn.view {
        color: #2f7fb0;
        background: #eef8ff;
      }
      .table-icon-btn.edit {
        color: #8a72da;
        background: #f3efff;
      }
      .table-icon-btn:disabled {
        opacity: 0.5;
      }
      .text-success.fw-semibold,
      .text-danger.fw-semibold,
      .text-secondary.fw-semibold {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 96px;
        padding: 5px 12px;
        border-radius: 999px;
        font-weight: 700;
      }
      .text-success.fw-semibold {
        background: #43b512;
        color: #fff !important;
      }
      .text-danger.fw-semibold {
        background: #ffebee;
        color: #d92d20 !important;
      }
      .text-secondary.fw-semibold {
        background: #eef2f7;
        color: #667085 !important;
      }
      @media (max-width: 1200px) {
        .filters-grid {
          grid-template-columns: repeat(3, minmax(140px, 1fr));
        }
        .filter-buttons {
          grid-column: 1 / -1;
          justify-content: flex-start;
        }
      }
      @media (max-width: 768px) {
        .filters-grid {
          grid-template-columns: 1fr;
        }
        .filter-buttons {
          grid-column: auto;
          flex-wrap: wrap;
        }
        .cta-btn {
          width: 100%;
        }
      }
    `,
  ],
})
export class ContractListPage {
  protected readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(ApiService);
  private readonly contractsService = inject(ContractsService);
  private readonly loaService = inject(LoaService);
  private readonly vendorsService = inject(VendorsService);
  private readonly chargesService = inject(ChargesService);
  private readonly toast = inject(ToastService);
  private readonly auth = inject(AuthService);
  protected readonly isVendorUser = computed(() => this.auth.user()?.role === 'vendor');
  protected readonly vendorId = computed(() => this.auth.user()?.vendor_id || '');

  protected readonly SearchIcon = Search;
  protected readonly PlusIcon = Plus;
  protected readonly FileDownIcon = FileDown;
  protected readonly FileTextIcon = FileText;
  protected readonly FileCheck2Icon = FileCheck2;
  protected readonly CalendarDaysIcon = CalendarDays;
  protected readonly FileClockIcon = FileClock;
  protected readonly Link2OffIcon = Link2Off;
  protected readonly EyeIcon = Eye;
  protected readonly PencilIcon = Pencil;
  protected readonly Trash2Icon = Trash2;
  protected readonly ChevronsLeftIcon = ChevronsLeft;
  protected readonly ChevronLeftIcon = ChevronLeft;
  protected readonly ChevronRightIcon = ChevronRight;
  protected readonly ChevronsRightIcon = ChevronsRight;
  protected readonly CloseIcon = X;

  protected readonly contracts = signal<Contract[]>([]);
  protected readonly loas = signal<Loa[]>([]);
  protected readonly vendors = signal<Map<string, Vendor>>(new Map());
  protected readonly rawApplications = signal<ApplicationApi[]>([]);
  protected readonly billConfigsByLoa = signal<Record<string, LoaBillConfig[]>>({});
  protected readonly loaSchedulesByLoa = signal<Record<string, LoaSchedule[]>>({});
  protected readonly chargeMasters = signal<Charge[]>([]);
  protected readonly contractAnalysisById = signal<Record<string, ContractAnalysisReportRow>>({});

  protected readonly draftSearch = signal('');
  protected readonly draftFromDate = signal('');
  protected readonly draftToDate = signal('');
  protected readonly draftStatus = signal('all');
  protected readonly draftContractStatus = signal('all');
  protected readonly appliedCategory = signal<ContractCategoryFilter>('all');
  protected readonly appliedBessOnly = signal(false);
  protected readonly appliedCreatedDate = signal('');
  protected readonly appliedScheduledToday = signal(true);

  protected readonly appliedSearch = signal('');
  protected readonly appliedFromDate = signal('');
  protected readonly appliedToDate = signal('');
  protected readonly appliedStatus = signal('all');
  protected readonly appliedContractStatus = signal('all');

  protected readonly page = signal(1);
  protected readonly pageSize = signal(10);
  protected readonly pageSizes = PAGE_SIZES;
  protected readonly loading = signal(true);
  protected readonly estimatedProfitModal = signal<EstimatedProfitModalData | null>(null);
  private loadGeneration = 0;

  protected readonly summaryTiles = computed(() => {
    const data = this.contracts();
    const pendingCount = data.filter((item) => this.normalizeStatus(item.status).includes('pending')).length;
    const activeCount = data.filter((item) => {
      const status = this.normalizeStatus(item.status);
      return status === 'approved' || status === 'active';
    }).length;
    const todayCount = data.filter((item) => this.isContractScheduledToday(item)).length;
    const unlinkedCount = data.filter((item) => {
      const compatibility = (item.compatibility_status || '').trim().toLowerCase();
      return compatibility.length === 0 || compatibility.includes('unlinked');
    }).length;
    return [
      {
        label: 'Total Contract',
        count: data.length,
        icon: this.FileTextIcon,
        categories: this.categoryMetrics(data, { includeBess: true }),
      },
      {
        label: 'Active Contract',
        count: activeCount,
        icon: this.FileCheck2Icon,
        categories: this.categoryMetrics(
          data.filter((item) => {
            const status = this.normalizeStatus(item.status);
            return status === 'approved' || status === 'active';
          }),
          { includeBess: true },
        ),
      },
      {
        label: "Today's Contract",
        count: todayCount,
        icon: this.CalendarDaysIcon,
        categories: this.categoryMetrics(data.filter((item) => this.isContractScheduledToday(item)), { includeBess: true }),
      },
      {
        label: 'Pending Contract',
        count: pendingCount,
        icon: this.FileClockIcon,
        categories: this.categoryMetrics(data.filter((item) => this.normalizeStatus(item.status).includes('pending')), { includeBess: true }),
      },
      {
        label: 'Unlinked LOI',
        count: unlinkedCount,
        icon: this.Link2OffIcon,
        categories: this.categoryMetrics(
          data.filter((item) => {
            const compatibility = (item.compatibility_status || '').trim().toLowerCase();
            return compatibility.length === 0 || compatibility.includes('unlinked');
          }),
        ).map((entry, index) => ({
          ...entry,
          label: index === 0 ? 'Buy' : index === 1 ? 'Sell' : 'Banking',
        })),
      },
    ];
  });

  protected readonly filteredContracts = computed(() => {
    const term = this.appliedSearch().trim().toLowerCase();
    const fromDate = this.appliedFromDate();
    const toDate = this.appliedToDate();
    const status = this.appliedStatus();
    const contractStatus = this.appliedContractStatus();
    const category = this.appliedCategory();
    const createdDate = this.appliedCreatedDate();
    const vendorId = this.vendorId();
    const bessOnly = this.appliedBessOnly();
    const scheduledToday = this.appliedScheduledToday();

    return this.contracts().filter((contract) => {
      const belongsToVendor =
        !vendorId || (contract.party_details || []).some((party) => (party.vendor_id || '') === vendorId);
      if (!belongsToVendor) return false;

      const normalizedStatus = this.normalizeStatus(contract.status);
      const searchable = [
        contract.contract_number,
        contract.contract_type,
        contract.compatibility_status || '',
        contract.status,
        this.getContractLifecycleStatusLabel(contract),
      ]
        .join(' ')
        .toLowerCase();

      const dateForFilter = this.normalizeDate(contract.created_at) || this.normalizeDate(contract.issue_date);
      const matchesSearch = !term || searchable.includes(term);
      const matchesStatus = status === 'all' || normalizedStatus === status;
      const matchesContractStatus = contractStatus === 'all' || this.getContractLifecycleStatus(contract) === contractStatus;
      const matchesCategory = category === 'all' || this.contractCategory(contract) === category;
      const matchesCreatedDate = !createdDate || dateForFilter === createdDate;
      const matchesFrom = !fromDate || (!!dateForFilter && dateForFilter >= fromDate);
      const matchesTo = !toDate || (!!dateForFilter && dateForFilter <= toDate);
      const matchesBess = !bessOnly || this.isBessContract(contract);
      const matchesScheduledToday = !scheduledToday || this.isContractScheduledToday(contract);
      return matchesSearch && matchesStatus && matchesContractStatus && matchesCategory && matchesCreatedDate && matchesFrom && matchesTo && matchesBess && matchesScheduledToday;
    });
  });

  protected readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filteredContracts().length / this.pageSize())));
  protected readonly pagedContracts = computed(() => {
    const safePage = Math.min(this.page(), this.totalPages());
    const start = (safePage - 1) * this.pageSize();
    return this.filteredContracts().slice(start, start + this.pageSize());
  });
  protected readonly startRow = computed(() => (this.filteredContracts().length === 0 ? 0 : (this.page() - 1) * this.pageSize() + 1));
  protected readonly endRow = computed(() => Math.min(this.page() * this.pageSize(), this.filteredContracts().length));
  constructor() {
    this.load();
    this.route.queryParamMap.subscribe((params) => {
      const nextCategory = this.parseCategoryFilter(params.get('category'));
      const nextContractStatus = this.parseContractStatusFilter(params.get('contractStatus'));
      if (nextCategory === 'all' && nextContractStatus === 'all') return;

      this.draftSearch.set('');
      this.draftFromDate.set('');
      this.draftToDate.set('');
      this.draftStatus.set('all');
      this.draftContractStatus.set(nextContractStatus);
      this.appliedSearch.set('');
      this.appliedFromDate.set('');
      this.appliedToDate.set('');
      this.appliedStatus.set('all');
      this.appliedContractStatus.set(nextContractStatus);
      this.appliedCategory.set(nextCategory);
      this.appliedBessOnly.set(false);
      this.appliedCreatedDate.set('');
      this.appliedScheduledToday.set(false);
      this.page.set(1);
    });
  }

  private load(): void {
    this.loading.set(true);
    const generation = ++this.loadGeneration;
    let pendingRequests = 6;
    const markLoaded = () => {
      if (generation !== this.loadGeneration) return;
      pendingRequests -= 1;
      if (pendingRequests <= 0) {
        this.loading.set(false);
      }
    };

    this.contractsService.list().subscribe({
      next: (contracts) => {
        this.contracts.set(contracts);
        this.preloadBillConfigsForContracts(contracts || []);
        this.preloadSchedulesForContracts(contracts || []);
        markLoaded();
      },
      error: () => {
        this.toast.show('Failed to load contracts', '', 'danger');
        markLoaded();
      },
    });
    this.contractsService.analysisReport().subscribe({
      next: (report) => {
        const map: Record<string, ContractAnalysisReportRow> = {};
        for (const row of report?.rows || []) {
          const id = String(row.contract_id || '').trim();
          if (id) map[id] = row;
        }
        this.contractAnalysisById.set(map);
        markLoaded();
      },
      error: () => {
        this.contractAnalysisById.set({});
        markLoaded();
      },
    });
    this.api.get<ApplicationApi[]>('/api/applications').subscribe({
      next: (applications) => {
        this.rawApplications.set(applications || []);
        markLoaded();
      },
      error: () => {
        this.rawApplications.set([]);
        markLoaded();
      },
    });
    this.loaService.list().subscribe({
      next: (loas) => {
        this.loas.set(loas || []);
        markLoaded();
      },
      error: () => {
        this.toast.show('Failed to load LOAs', '', 'danger');
        markLoaded();
      },
    });
    this.chargesService.list().subscribe({
      next: (charges) => {
        this.chargeMasters.set(charges || []);
        markLoaded();
      },
      error: () => {
        this.chargeMasters.set([]);
        markLoaded();
      },
    });
    this.vendorsService.list().subscribe({
      next: (vendors) => {
        this.vendors.set(new Map((vendors || []).map((vendor) => [vendor.id, vendor])));
        markLoaded();
      },
      error: () => {
        this.vendors.set(new Map());
        markLoaded();
      },
    });
  }

  protected applyFilters(): void {
    this.appliedSearch.set(this.draftSearch().trim());
    this.appliedFromDate.set(this.draftFromDate());
    this.appliedToDate.set(this.draftToDate());
    this.appliedStatus.set(this.draftStatus());
    this.appliedContractStatus.set(this.draftContractStatus());
    this.appliedScheduledToday.set(false);
    this.page.set(1);
  }

  protected resetFilters(): void {
    this.draftSearch.set('');
    this.draftFromDate.set('');
    this.draftToDate.set('');
    this.draftStatus.set('all');
    this.draftContractStatus.set('all');
    this.appliedSearch.set('');
    this.appliedFromDate.set('');
    this.appliedToDate.set('');
    this.appliedStatus.set('all');
    this.appliedContractStatus.set('all');
    this.appliedCategory.set('all');
    this.appliedBessOnly.set(false);
    this.appliedCreatedDate.set('');
    this.appliedScheduledToday.set(true);
    this.page.set(1);
  }

  protected applySummaryCategoryFilter(tileLabel: string, categoryKey: SummaryCategoryKey, categoryLabel: string): void {
    if (!this.canFilterSummaryCategory(tileLabel)) return;
    if (tileLabel === 'Unlinked LOI') {
      void this.router.navigate(['/loas'], {
        queryParams: {
          summary: 'pending_linking',
          category: this.mapUnlinkedLoaLabelToCategory(categoryLabel),
        },
      });
      return;
    }
    this.appliedBessOnly.set(categoryKey === 'bess');
    this.appliedCategory.set(categoryKey === 'bess' ? 'all' : this.mapCategoryKeyToFilter(categoryKey));
    this.appliedStatus.set('all');
    this.appliedContractStatus.set('all');
    this.appliedCreatedDate.set('');
    this.appliedScheduledToday.set(false);
    if (tileLabel === 'Active Contract') {
      this.appliedContractStatus.set('active');
    } else if (tileLabel === "Today's Contract") {
      this.appliedScheduledToday.set(true);
    } else if (tileLabel === 'Pending Contract') {
      this.appliedStatus.set('pending');
    }
    this.page.set(1);
  }

  protected openVendorDetails(vendorId?: string | null): void {
    const id = (vendorId || '').trim();
    if (!id) return;
    const url = this.router.serializeUrl(this.router.createUrlTree(['/vendors', id]));
    window.open(url, '_blank');
  }

  protected openLoaDetails(loaId?: string | null): void {
    const id = (loaId || '').trim();
    if (!id) return;
    const url = this.router.serializeUrl(this.router.createUrlTree(['/loas', id], { queryParams: { source: 'contract' } }));
    window.open(url, '_blank');
  }


  protected isSummaryCategoryActive(tileLabel: string, categoryKey: SummaryCategoryKey): boolean {
    if (!this.canFilterSummaryCategory(tileLabel)) return false;
    const categoryMatches = categoryKey === 'bess' ? this.appliedBessOnly() : this.appliedCategory() === this.mapCategoryKeyToFilter(categoryKey);
    if (tileLabel === 'Active Contract') return categoryMatches && this.appliedContractStatus() === 'active';
    if (tileLabel === "Today's Contract") {
      return categoryMatches && this.appliedScheduledToday();
    }
    if (tileLabel === 'Pending Contract') return categoryMatches && this.appliedStatus() === 'pending';
    return categoryMatches;
  }

  protected canFilterSummaryCategory(tileLabel: string): boolean {
    return tileLabel === 'Total Contract' || tileLabel === 'Active Contract' || tileLabel === "Today's Contract" || tileLabel === 'Pending Contract' || tileLabel === 'Unlinked LOI';
  }

  protected onPageSizeChange(value: string): void {
    this.pageSize.set(Number(value));
    this.page.set(1);
  }

  protected goFirst(): void {
    this.page.set(1);
  }

  protected goLast(): void {
    this.page.set(this.totalPages());
  }

  protected exportContracts(): void {
    const headers = [
      'S.No.',
      'Contract Number',
      'Contract Type',
      'Contract Category',
      'Created Date',
      'Contract Period',
      'Seller',
      'Seller LOA',
      'Buyer',
      'Buyer LOA',
      'Import Quantum (MWh)',
      'Export Quantum (MWh)',
      'Return Percentage',
      'Contract Status',
      'Compatibility Status',
      'Approval Status',
    ];
    const rows = this.filteredContracts().map((contract, index) => {
      const row = this.rowDisplay(contract);
      return [
      index + 1,
      contract.contract_number,
      contract.contract_type,
      this.contractCategory(contract),
      this.formatDate(contract.created_at),
      `${this.formatDate(contract.period_from)} - ${this.formatDate(contract.period_to)}`,
      row.seller,
      row.sellerLoa,
      row.buyer,
      row.buyerLoa,
      row.importQuantum,
      row.exportQuantum,
      this.formatReturnPercentage(row.returnPercentage),
      this.getContractLifecycleStatusLabel(contract),
      contract.compatibility_status || 'Pending',
      this.getStatusLabel(contract.status),
      ];
    });
    const csv = [headers.join(','), ...rows.map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `contracts_export_${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  protected canEdit(contract: Contract): boolean {
    if (this.isVendorUser()) return false;
    const isPending = this.normalizeStatus(contract.status).includes('pending');
    const isCreator = (contract.created_by || '') === (this.auth.user()?.id || '');
    return isPending && isCreator && !this.hasLinkedApplications(contract.id);
  }

  protected editButtonTitle(contract: Contract): string {
    if (this.isVendorUser()) return 'Vendor users cannot edit contracts';
    if (this.hasLinkedApplications(contract.id)) return 'Contracts with linked applications cannot change LOA mapping';
    if (!this.normalizeStatus(contract.status).includes('pending')) return 'Only pending contracts can be edited';
    if ((contract.created_by || '') !== (this.auth.user()?.id || '')) return 'Only contract creator can edit';
    return 'Edit';
  }

  protected canDelete(contract: Contract): boolean {
    return !this.isVendorUser() && !!contract.can_delete;
  }

  protected deleteButtonTitle(contract: Contract): string {
    if (this.isVendorUser()) return 'Vendor users cannot delete contracts';
    if (!contract.can_delete) return 'Contract is used in an application';
    return 'Delete';
  }

  protected deleteContract(contract: Contract): void {
    if (!this.canDelete(contract)) {
      this.toast.show('Contract cannot be deleted because it is used in an application', '', 'danger');
      return;
    }
    if (!confirm(`Delete Contract ${contract.contract_number}?`)) return;
    this.contractsService.delete(contract.id).subscribe({
      next: () => {
        this.toast.show('Contract deleted', '', 'success');
        this.load();
      },
      error: (error) => {
        const detail = error?.error?.detail || '';
        this.toast.show(detail || 'Failed to delete contract', '', 'danger');
      },
    });
  }

  protected getStatusClass(status: string): string {
    const normalized = this.normalizeStatus(status);
    if (normalized === 'approved' || normalized === 'active') return 'text-success fw-semibold';
    if (normalized.includes('rejected')) return 'text-danger fw-semibold';
    if (normalized === 'draft') return 'text-secondary fw-semibold';
    return 'status-pending';
  }

  protected getStatusLabel(status: string): string {
    return status
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  protected getContractLifecycleStatus(contract: Contract): ContractLifecycleStatus {
    const start = this.parseDateOnly(contract.period_from);
    const end = this.parseDateOnly(contract.period_to);
    const today = this.todayAtMidnight();

    if (!start || !end) return 'unknown';
    if (end < today) return 'expired';
    if (start <= today && end >= today) return 'active';
    if (start > today && this.isNextCalendarMonth(start, today)) return 'starts_next_month';
    if (start > today) return 'upcoming';
    return 'unknown';
  }

  protected getContractLifecycleStatusLabel(contract: Contract): string {
    switch (this.getContractLifecycleStatus(contract)) {
      case 'active':
        return 'Active';
      case 'expired':
        return 'Expired';
      case 'starts_next_month':
        return 'Starts Next Month';
      case 'upcoming':
        return 'Upcoming';
      default:
        return 'Unknown';
    }
  }

  protected getContractLifecycleStatusClass(contract: Contract): string {
    switch (this.getContractLifecycleStatus(contract)) {
      case 'active':
        return 'status-active';
      case 'expired':
        return 'status-expired';
      case 'starts_next_month':
        return 'status-starts-next-month';
      case 'upcoming':
        return 'status-upcoming';
      default:
        return 'status-unknown';
    }
  }

  protected formatReturnPercentage(value: string | number | null | undefined): string {
    if (value === null || value === undefined) return 'NA';
    const normalized = String(value).trim();
    return normalized ? normalized : 'NA';
  }

  protected formatProfitValue(value: number | null | undefined): string {
    if (value === null || value === undefined) return 'NA';
    return `Rs ${this.formatNumericValue(value)}`;
  }

  protected profitValueClass(value: number | null | undefined): string {
    if (value === null || value === undefined) return 'profit-value-neutral';
    if (value < 0) return 'profit-value-negative';
    return 'profit-value-positive';
  }

  protected formatDate(value: string | null | undefined): string {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  protected contractCategory(contract: Contract): string {
    const savedCategory = (contract.contract_category || '').trim().toUpperCase();
    if (savedCategory) return savedCategory;
    const contractType = contract.contract_type.toLowerCase();
    if (contractType.includes('bank')) return 'BANKING';
    if (contractType.includes('hybrid')) return 'HYBRID';
    return 'SALE-PURCHASE';
  }

  private isBessContract(contract: Contract): boolean {
    const parties = contract.party_details || [];
    const hasBessEntity = parties.some((party) => (party.entity_type || '').trim().toUpperCase() === 'BESS');
    if (hasBessEntity) return true;

    const partyVendorIds = parties
      .map((party) => (party.vendor_id || '').trim())
      .filter((id) => !!id);
    return partyVendorIds.some((vendorId) => this.isBessGeneratorType(this.vendors().get(vendorId)?.generator_type));
  }

  private isBessGeneratorType(value?: string | null): boolean {
    const generatorType = (value || '').trim().toLowerCase();
    return generatorType === 'bess with solar' || generatorType === 'bess without solar';
  }

  protected rowDisplay(contract: Contract) {
    const parties = contract.party_details || [];
    const sellerParty = parties.find((item) => this.resolveContractPartyKind(item) === 'sell') || parties[0];
    const buyerParty = parties.find((item) => this.resolveContractPartyKind(item) === 'buy') || parties[1] || parties[0];
    const sellerLoa = sellerParty?.loa_id ? this.loas().find((item) => item.id === sellerParty.loa_id) : null;
    const buyerLoa = buyerParty?.loa_id ? this.loas().find((item) => item.id === buyerParty.loa_id) : null;
    const sellerVendorId = sellerParty?.vendor_id || sellerLoa?.vendor_id || '';
    const buyerVendorId = buyerParty?.vendor_id || buyerLoa?.vendor_id || '';
    const importQuantum = this.quantumDisplay(this.contractQuantumMwhByKind(contract, 'buy'));
    const exportQuantum = this.quantumDisplay(this.contractQuantumMwhByKind(contract, 'sell'));

    return {
      seller: this.vendors().get(sellerVendorId)?.vendor_name || '-',
      sellerVendorId,
      sellerLoa: sellerLoa?.loa_number || '-',
      sellerLoaId: sellerLoa?.id || '',
      buyer: this.vendors().get(buyerVendorId)?.vendor_name || '-',
      buyerVendorId,
      buyerLoa: buyerLoa?.loa_number || '-',
      buyerLoaId: buyerLoa?.id || '',
      importQuantum,
      exportQuantum,
      returnPercentage: '',
    };
  }

  protected applicationCount(contractId: string): number {
    return this.rawApplications().filter((application) => String(application.contract_id) === String(contractId)).length;
  }

  protected hasLinkedApplications(contractId: string): boolean {
    return this.applicationCount(contractId) > 0;
  }

  protected applicationTooltip(contractId: string): string {
    const numbers = this.rawApplications()
      .filter((application) => String(application.contract_id) === String(contractId))
      .map((application) => (application.application_number || '').trim())
      .filter((value) => !!value);
    if (!numbers.length) return 'No applications linked';
    return `Applications:\n${numbers.join('\n')}`;
  }

  protected estimatedProfit(contract: Contract): number | null {
    return this.buildEstimatedProfitSummary(contract).netProfit;
  }

  protected openEstimatedProfitModal(contract: Contract): void {
    const summary = this.buildEstimatedProfitSummary(contract);
    const details: Array<{ label: string; value: string }> = [];

    for (const row of summary.chargeRows) {
      const chargeLabel = `${row.chargeType}${row.rateType ? ' (' + row.rateType + ')' : ''}`;
      details.push({
        label: `${chargeLabel}`,
        value: `${this.formatProfitValue(row.totalAmount)} | Buyer ${this.formatProfitValue(row.buyerAmount)} | Seller ${this.formatProfitValue(row.sellerAmount)} | SECI ${this.formatProfitValue(row.seciAmount)} | ${row.allocationLabel}`,
      });
    }

    this.estimatedProfitModal.set({
      title: 'Estimated Profit',
      subtitle: contract.contract_number || 'Contract',
      equation:
        `${this.formatProfitValue(summary.netProfit)} = ` +
        `Buyer Cost ${this.formatProfitValue(summary.revenue)} - ` +
        `Seller Cost ${this.formatProfitValue(summary.cost)} - ` +
        `Application Charges ${this.formatProfitValue(summary.applicationCharges)}`,
      hint: summary.chargeRows.length
        ? 'Application charges are split to buyer and seller when mapped in OA charge allocation. Any unmapped balance is counted as SECI and reduced from Estimated Profit.'
        : 'No application charge bifurcation was found for this contract.',
      summaryLine:
        `Buyer Cost ${this.formatProfitValue(summary.revenue)} | ` +
        `Seller Cost ${this.formatProfitValue(summary.cost)} | ` +
        `Application Charges ${this.formatProfitValue(summary.applicationCharges)} | ` +
        `Net Estimated Profit ${this.formatProfitValue(summary.netProfit)}`,
      details,
    });
  }

  protected openActualProfitModal(contract: Contract): void {
    const breakdown = this.actualProfitBreakdown(contract);
    if (!breakdown) return;

    const { grossActualProfit, displayCharges, applicationChargeTotal, netActualProfit } = breakdown;

    const details: Array<{ label: string; value: string }> = [];
    for (const row of displayCharges) {
      const chargeLabel = `${row.chargeType}${row.rateType ? ' (' + row.rateType + ')' : ''}`;
      details.push({
        label: `${chargeLabel}`,
        value: `${this.formatProfitValue(row.totalAmount)} | Buyer ${this.formatProfitValue(row.buyerAmount)} | Seller ${this.formatProfitValue(row.sellerAmount)} | SECI ${this.formatProfitValue(row.seciAmount)} | ${row.allocationLabel}`,
      });
    }

    this.estimatedProfitModal.set({
      title: 'Actual Profit',
      subtitle: contract.contract_number || 'Contract',
      equation:
        `${this.formatProfitValue(netActualProfit)} = ` +
        `Gross Actual Profit ${this.formatProfitValue(grossActualProfit)} - ` +
        `Application Charges ${this.formatProfitValue(applicationChargeTotal)}`,
      hint: displayCharges.length
        ? 'Application charges shown here use approved quantum for MWh-based charges and follow the same charge mapping used in Estimated Profit.'
        : 'No application charge bifurcation was found for this contract.',
      summaryLine:
        `Gross Actual Profit ${this.formatProfitValue(grossActualProfit)} | ` +
        `Application Charges ${this.formatProfitValue(applicationChargeTotal)} | ` +
        `Net Actual Profit ${this.formatProfitValue(netActualProfit)}`,
      details,
    });
  }

  protected closeEstimatedProfitModal(): void {
    this.estimatedProfitModal.set(null);
  }

  protected actualProfit(contract: Contract): number | null {
    return this.actualProfitBreakdown(contract)?.netActualProfit ?? null;
  }

  private actualProfitBreakdown(contract: Contract): {
    grossActualProfit: number;
    displayCharges: EstimatedProfitChargeRow[];
    applicationChargeTotal: number;
    netActualProfit: number;
  } | null {
    const analysis = this.contractAnalysisById()[String(contract.id || '').trim()];
    const backendActual = analysis?.actual_profit;
    if (backendActual === null || backendActual === undefined) return null;

    const base = Number(backendActual);
    if (!Number.isFinite(base)) return null;

    const grossActualProfit = Number(base.toFixed(2));
    const displayCharges = this.resolveActualProfitChargeRows(contract, analysis);
    const applicationChargeTotal = Number(displayCharges.reduce((sum, row) => sum + row.totalAmount, 0).toFixed(2));
    const netActualProfit = Number((base - applicationChargeTotal).toFixed(2));

    return {
      grossActualProfit,
      displayCharges,
      applicationChargeTotal,
      netActualProfit,
    };
  }

  private resolveActualProfitChargeRows(contract: Contract, analysis: ContractAnalysisReportRow | null | undefined): EstimatedProfitChargeRow[] {
    const rows = this.resolveEstimatedProfitChargeRows(contract).map((row) => ({ ...row }));
    const requestedMw = Number(analysis?.requested_quantum_mw || 0);
    const approvedMw = Number(analysis?.approved_quantum_mw || 0);
    const ratio = Number.isFinite(requestedMw) && requestedMw > 0 && Number.isFinite(approvedMw) && approvedMw >= 0 ? approvedMw / requestedMw : 0;
    const safeRatio = Math.max(ratio, 0);

    if (approvedMw <= 0) {
      return rows.map((row) => ({
        ...row,
        totalAmount: 0,
        buyerAmount: 0,
        sellerAmount: 0,
        seciAmount: 0,
        allocationLabel: `${row.allocationLabel}${row.allocationLabel ? ' • ' : ''}Approved quantum is 0, so actual charge is 0`,
      }));
    }

    return rows.map((row) => {
      if (String(row.rateType || '').trim() !== 'Rate (₹/MWh)') return row;
      return {
        ...row,
        totalAmount: Number((row.totalAmount * safeRatio).toFixed(2)),
        buyerAmount: Number((row.buyerAmount * safeRatio).toFixed(2)),
        sellerAmount: Number((row.sellerAmount * safeRatio).toFixed(2)),
        seciAmount: Number((row.seciAmount * safeRatio).toFixed(2)),
        allocationLabel: `${row.allocationLabel}${row.allocationLabel ? ' • ' : ''}Approved quantum scaling ${this.formatNumericValue(safeRatio * 100)}%`,
      };
    });
  }

  private buildEstimatedProfitSummary(contract: Contract): EstimatedProfitSummary {
    const parties = contract.party_details || [];
    if (!parties.length) {
      return { revenue: 0, cost: 0, applicationCharges: 0, netProfit: null, chargeRows: [] };
    }

    const contractStart = this.parseDateOnly(contract.period_from);
    const contractEnd = this.parseDateOnly(contract.period_to);
    if (!contractStart || !contractEnd) {
      return { revenue: 0, cost: 0, applicationCharges: 0, netProfit: null, chargeRows: [] };
    }

    let hasKnownContribution = false;
    let revenue = 0;
    let cost = 0;

    for (const party of parties) {
      const loa = this.loas().find((item) => item.id === party.loa_id);
      const loaType = (party.party_type || this.resolveLoaTypeFromLoa(loa)).toLowerCase().trim();
      if (loaType === 'banking') continue;

      const rate = this.resolveEstimatedRate(loa, this.billConfigsByLoa()[party.loa_id] || []);
      if (rate === null) continue;

      const contractedUnits = this.resolveEstimatedContractUnits(contractStart, contractEnd, loa, party.quantum);
      if (!contractedUnits) continue;

      hasKnownContribution = true;
      if (loaType === 'buy') revenue += contractedUnits * rate;
      if (loaType === 'sell') cost += contractedUnits * rate;
    }

    const chargeRows = this.resolveEstimatedProfitChargeRows(contract);
    const applicationCharges = chargeRows.reduce((sum, row) => sum + row.totalAmount, 0);
    return {
      revenue,
      cost,
      applicationCharges,
      netProfit: hasKnownContribution ? revenue - cost - applicationCharges : null,
      chargeRows,
    };
  }

  private resolveEstimatedProfitChargeRows(contract: Contract): EstimatedProfitChargeRow[] {
    const summary = buildContractChargeBreakdown({
      contractId: String(contract.id || '').trim(),
      cutoffDate: String(contract.period_from || contract.issue_date || contract.created_at || '').trim(),
      periodFrom: String(contract.period_from || '').trim(),
      periodTo: String(contract.period_to || '').trim(),
      partyDetails: (contract.party_details || []).map((party) => ({
        loaId: String(party.loa_id || '').trim(),
        quantum: party.quantum ?? null,
        rldc: party.rldc || null,
      })) as ContractChargePartyLike[],
      applications: this.rawApplications(),
      chargeMasters: this.chargeMasters(),
      billConfigsByLoa: this.billConfigsByLoa(),
      loaSchedulesByLoa: this.loaSchedulesByLoa(),
    });
    return summary.rows.map((row) => ({ ...row }));
  }

  private resolvePunchedProfitChargeRows(contract: Contract): EstimatedProfitChargeRow[] {
    const rows: EstimatedProfitChargeRow[] = [];
    const masters = this.configuredChargesForContract(contract);
    const basis = this.resolveContractChargeBasis(contract);

    for (const application of this.rawApplications()) {
      if (String(application.contract_id || '').trim() !== String(contract.id)) continue;
      const approvalCharges = application.approval_charges || [];
      if (!approvalCharges.length) continue;

      const allocationMap = this.oaChargeAllocationMap(application);

      for (const [index, charge] of approvalCharges.entries()) {
        const allocationKey = this.normalizeApplicationChargeType(charge, index);
        const master = this.resolveChargeMasterFromChargeMasters(masters, allocationKey, contract);
        const rateType = (master?.rate_type || '').trim() || null;
        const masterValue = master ? Number(master.value) : Number.NaN;
        const fallbackAmount = this.resolveApplicationChargeAmount(charge);
        const displayChargeName = String(master?.charge_name || '').trim() || allocationKey;
        const regionLabel = master?.charge_name || allocationKey;
        const requiredRegion = this.extractRegionCode(regionLabel);
        if (requiredRegion && !this.shouldApplyRegionalCharge(contract, regionLabel)) continue;
        const totalAmount =
          master && Number.isFinite(masterValue) ? this.resolveChargeAmountFromRateType(masterValue, rateType, basis) : fallbackAmount;
        const allocation = allocationMap.get(allocationKey);
        const buyerPercent = this.sanitizePercent(allocation?.buyer_percent);
        const sellerPercent = this.cappedSellerPercent(allocation?.seller_percent, buyerPercent);
        const mappedPercent = buyerPercent + sellerPercent;
        const buyerAmount = (totalAmount * buyerPercent) / 100;
        const sellerAmount = (totalAmount * sellerPercent) / 100;
        const seciAmount = Math.max(totalAmount - buyerAmount - sellerAmount, 0);
        const effectiveDate = String(master?.effective_date || '').trim();
        rows.push({
          applicationNumber: application.application_number || String(application.id),
          chargeType: displayChargeName,
          rateType,
          totalAmount,
          buyerAmount,
          sellerAmount,
          seciAmount,
          allocationLabel: !allocation
            ? 'No buyer/seller mapping found, so the remaining amount is counted on SECI'
            : `${effectiveDate ? 'Effective ' + effectiveDate + ' • ' : ''}Buyer ${this.formatNumericValue(buyerPercent)}% / Seller ${this.formatNumericValue(sellerPercent)}% / SECI ${this.formatNumericValue(Math.max(100 - mappedPercent, 0))}%`,
        });
      }
    }

    return rows;
  }

  private aggregateProfitChargeRows(rows: EstimatedProfitChargeRow[]): EstimatedProfitChargeRow[] {
    const grouped = new Map<string, EstimatedProfitChargeRow>();

    for (const row of rows) {
      const key = [row.chargeType, row.rateType || '', row.allocationLabel].join('|');
      const existing = grouped.get(key);
      if (!existing) {
        grouped.set(key, { ...row });
        continue;
      }

      existing.totalAmount = Number((existing.totalAmount + row.totalAmount).toFixed(2));
      existing.buyerAmount = Number((existing.buyerAmount + row.buyerAmount).toFixed(2));
      existing.sellerAmount = Number((existing.sellerAmount + row.sellerAmount).toFixed(2));
      existing.seciAmount = Number((existing.seciAmount + row.seciAmount).toFixed(2));
    }

    return [...grouped.values()];
  }

  private resolveConfiguredProfitChargeRows(contract: Contract): EstimatedProfitChargeRow[] {
    const charges = this.configuredChargesForContract(contract);
    if (!charges.length) return [];

    const loaIds = (contract.party_details || [])
      .map((party) => String(party.loa_id || '').trim())
      .filter((id) => !!id);
    const allocationMap = this.oaChargeAllocationMapFromLoaIds(loaIds);

    const rows: EstimatedProfitChargeRow[] = [];
    for (const charge of charges) {
      const requiredRegion = this.extractRegionCode(charge.charge_name);
      if (requiredRegion && !this.shouldApplyRegionalCharge(contract, charge.charge_name)) continue;

      const amount = this.resolveConfiguredChargeAmount(contract, charge);
      if (!Number.isFinite(amount) || amount <= 0) continue;

      const chargeType = this.normalizeChargeAllocationType(`${charge.charge_name || ''} ${charge.charge_type || ''}`.trim());
      const displayChargeName = String(charge.charge_name || '').trim() || chargeType || 'Charge';
      const allocation = allocationMap.get(chargeType);
      const buyerPercent = this.sanitizePercent(allocation?.buyer_percent);
      const sellerPercent = this.cappedSellerPercent(allocation?.seller_percent, buyerPercent);
      const mappedPercent = buyerPercent + sellerPercent;
      const buyerAmount = (amount * buyerPercent) / 100;
      const sellerAmount = (amount * sellerPercent) / 100;
      const seciAmount = Math.max(amount - buyerAmount - sellerAmount, 0);
      rows.push({
        applicationNumber: String(charge.charge_name || '').trim() || 'Charge Master',
        chargeType: displayChargeName,
        rateType: (charge.rate_type || '').trim() || null,
        totalAmount: Number(amount.toFixed(2)),
        buyerAmount: Number(buyerAmount.toFixed(2)),
        sellerAmount: Number(sellerAmount.toFixed(2)),
        seciAmount: Number(seciAmount.toFixed(2)),
        allocationLabel: !allocation
          ? `Effective ${charge.effective_date} • No buyer/seller mapping found, so the remaining amount is counted on SECI`
          : `Effective ${charge.effective_date} • Buyer ${this.formatNumericValue(buyerPercent)}% / Seller ${this.formatNumericValue(sellerPercent)}% / SECI ${this.formatNumericValue(Math.max(100 - mappedPercent, 0))}%`,
      });
    }
    return rows;
  }

  private extractRegionCode(value: string | null | undefined): string | null {
    const text = String(value || '').trim().toUpperCase();
    if (!text) return null;

    const explicit = /\b(ERLDC|NERLDC|NRLDC|SRLDC|WRLDC)\b/.exec(text);
    if (explicit) return explicit[1];

    if (/\bNORTH\s*EAST\b/.test(text) || /\bNORTHEAST\b/.test(text)) return 'NERLDC';
    if (/\bEAST\b/.test(text) || /\bEASTERN\b/.test(text)) return 'ERLDC';
    if (/\bNORTH\b/.test(text) || /\bNORTHERN\b/.test(text)) return 'NRLDC';
    if (/\bSOUTH\b/.test(text) || /\bSOUTHERN\b/.test(text)) return 'SRLDC';
    if (/\bWEST\b/.test(text) || /\bWESTERN\b/.test(text)) return 'WRLDC';

    return null;
  }

  private contractRegionCodes(contract: Contract): Set<string> {
    const set = new Set<string>();
    for (const party of contract.party_details || []) {
      const code = this.extractRegionCode(party.rldc);
      if (code) set.add(code);
    }
    return set;
  }

  private shouldApplyRegionalCharge(contract: Contract, chargeLabel: string | null | undefined): boolean {
    const required = this.extractRegionCode(chargeLabel);
    if (!required) return true;
    return this.contractRegionCodes(contract).has(required);
  }

  private resolveChargeMasterFromChargeMasters(masters: Charge[], chargeLabel: string, contract?: Contract): Charge | null {
    const needle = String(chargeLabel || '').trim().toUpperCase();
    if (!needle) return null;

    const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const containsNeedle = new RegExp(`\\b${escaped}\\b`, 'i');

    const normalizedNeedle = this.normalizeChargeAllocationType(chargeLabel);
    let best: Charge | null = null;
    for (const row of masters || []) {
      const group = String(row?.charge_type || '').trim();
      const name = String(row?.charge_name || '').trim();
      if (!group && !name) continue;

      const normalizedRow = this.normalizeChargeAllocationType(`${name} ${group}`.trim());
      const matchesGroup = !!group && (group.trim().toUpperCase() === needle || containsNeedle.test(group));
      const matchesName = !!name && (name.trim().toUpperCase() === needle || containsNeedle.test(name));
      const matchesNormalized = !!normalizedNeedle && normalizedRow === normalizedNeedle;
      if (!matchesGroup && !matchesName && !matchesNormalized) continue;
      if (contract && !this.shouldApplyRegionalCharge(contract, name || group)) continue;

      if (!best || String(row.effective_date || '') > String(best.effective_date || '')) best = row;
    }

    return best;
  }

  private resolveContractChargeBasis(contract: Contract): { days: number; energyMwh: number } {
    const contractStart = this.parseDateOnly(contract.period_from);
    const contractEnd = this.parseDateOnly(contract.period_to);
    if (!contractStart || !contractEnd) return { days: 0, energyMwh: 0 };

    const daysDifference = this.inclusiveDayCount(contractStart, contractEnd);

    const energyByLoa = new Map<string, number>();
    for (const party of contract.party_details || []) {
      const loaId = String(party.loa_id || '').trim();
      if (!loaId || energyByLoa.has(loaId)) continue;

      const schedules = this.loaSchedulesByLoa()[loaId] || [];
      const scheduleEnergy = schedules.reduce((total, schedule) => {
        const scheduleStart = this.parseDateOnly(schedule.from_date);
        const scheduleEnd = this.parseDateOnly(schedule.to_date);
        if (!scheduleStart || !scheduleEnd) return total;

        const overlapDays = this.overlapDayCount(contractStart, contractEnd, scheduleStart, scheduleEnd);
        if (!overlapDays) return total;

        const timeDifference = this.timeDifferenceHours(schedule.from_time, schedule.to_time);
        if (!timeDifference) return total;

        const quantum = this.firstFiniteNumber(schedule.quantum_mw, party.quantum);
        if (quantum === null) return total;

        return total + overlapDays * timeDifference * quantum;
      }, 0);

      if (scheduleEnergy > 0) {
        energyByLoa.set(loaId, scheduleEnergy);
        continue;
      }

      const quantum = this.firstFiniteNumber(party.quantum);
      energyByLoa.set(loaId, quantum === null ? 0 : daysDifference * 24 * quantum);
    }

    const energyMwh = energyByLoa.size > 0 ? Math.max(...energyByLoa.values()) : 0;
    return { days: daysDifference, energyMwh };
  }

  private resolveChargeAmountFromRateType(value: number, rateType: string | null | undefined, basis: { days: number; energyMwh: number }): number {
    if (!Number.isFinite(value) || value < 0) return 0;
    const normalized = String(rateType || '').trim();
    if (normalized === 'Rate (₹/MWh)') return value * basis.energyMwh;
    if (normalized === 'Rate (₹/Day)') return value * basis.days;
    return value;
  }

  private resolveConfiguredChargeAmount(contract: Contract, charge: Charge): number {
    const value = Number(charge.value);
    if (!Number.isFinite(value) || value < 0) return 0;
    const basis = this.resolveContractChargeBasis(contract);
    const computed = this.resolveChargeAmountFromRateType(value, charge.rate_type, basis);
    return Number((Number.isFinite(computed) ? computed : 0).toFixed(2));
  }

  private configuredChargeCutoff(contract: Contract): string {
    return (
      this.normalizeDate(contract.period_from) ||
      this.normalizeDate(contract.issue_date) ||
      this.normalizeDate(contract.created_at) ||
      ''
    );
  }

  private configuredChargesForContract(contract: Contract): Charge[] {
    const cutoff = this.configuredChargeCutoff(contract);
    if (!cutoff) return [];

    const activeCharges = (this.chargeMasters() || [])
      .filter((row) => !!row && !!row.active)
      .filter((row) => String(row.effective_date || '').trim() !== '' && String(row.effective_date).trim() <= cutoff);

    const byKey = new Map<string, Charge>();
    for (const row of activeCharges) {
      const group = String(row.charge_type || '').trim().toUpperCase();
      const name = String(row.charge_name || '').trim().toUpperCase();
      const key = `${group}|${name}`;
      const existing = byKey.get(key);
      if (!existing || String(row.effective_date || '') > String(existing.effective_date || '')) {
        byKey.set(key, row);
      }
    }
    return [...byKey.values()].sort((left, right) => {
      const groupCompare = String(left.charge_type || '').localeCompare(String(right.charge_type || ''));
      if (groupCompare !== 0) return groupCompare;
      return String(left.charge_name || '').localeCompare(String(right.charge_name || ''));
    });
  }

  private parseOaChargeAllocation(raw: string | null | undefined): OaChargeAllocation[] {
    if (!raw || !raw.trim()) return [];
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((item) => {
          if (!item || typeof item !== 'object') return null;
          const charge_type = this.normalizeChargeAllocationType(String((item as Partial<OaChargeAllocation>).charge_type || '').trim());
          if (!charge_type) return null;
          const payerInput = String((item as Partial<OaChargeAllocation>).payer || 'Shared');
          const payer: OaChargeAllocation['payer'] =
            payerInput === 'Buyer' || payerInput === 'Seller' || payerInput === 'Shared' ? payerInput : 'Shared';
          const buyer = Number((item as Partial<OaChargeAllocation>).buyer_percent);
          const seller = Number((item as Partial<OaChargeAllocation>).seller_percent);
          return {
            charge_type,
            payer,
            buyer_percent: Number.isFinite(buyer) ? buyer : 0,
            seller_percent: Number.isFinite(seller) ? seller : 0,
          };
        })
        .filter((item): item is OaChargeAllocation => item !== null);
    } catch {
      return [];
    }
  }

  private latestOaChargeAllocation(loaId: string): string | null {
    if (!loaId) return null;
    const configs = this.billConfigsByLoa()[loaId] || [];
    const latest = [...configs]
      .filter((item) => (item.bill_type || '').trim().toUpperCase() === 'OA')
      .sort((left, right) => (right.effective_date || '').localeCompare(left.effective_date || ''))[0];
    return latest?.oa_charge_allocation || null;
  }

  private oaChargeAllocationMapFromLoaIds(loaIds: string[]): Map<string, OaChargeAllocation> {
    const uniqueLoaIds = (loaIds || []).map((id) => String(id || '').trim()).filter((id) => !!id);
    const map = new Map<string, OaChargeAllocation>();
    for (const loaId of uniqueLoaIds) {
      const items = this.parseOaChargeAllocation(this.latestOaChargeAllocation(loaId));
      for (const item of items) {
        if (!map.has(item.charge_type)) {
          map.set(item.charge_type, item);
        }
      }
    }
    return map;
  }

  private oaChargeAllocationMap(application: ApplicationApi): Map<string, OaChargeAllocation> {
    const loaIds = [String(application.seller_loa_id || '').trim(), String(application.buyer_loa_id || '').trim()].filter((id) => !!id);
    return this.oaChargeAllocationMapFromLoaIds(loaIds);
  }

  private normalizeChargeAllocationType(value: string): string {
    const normalized = value.trim().toUpperCase();
    if (!normalized) return '';
    if (normalized.includes('TRANSMISSION')) return 'Transmission';
    if (normalized.includes('WHEEL')) return 'Wheeling';
    if (normalized.includes('SLDC')) return 'SLDC';
    if (normalized.includes('RLDC')) return 'RLDC';
    if (normalized.includes('LOSS')) return 'Loss';
    if (normalized.includes('APP')) return 'App';
    return value.trim();
  }

  private normalizeApplicationChargeType(item: ApplicationChargeApi, index: number): string {
    const raw = `${item.sub_group_name || ''} ${item.group_name || ''} ${item.section_heading || ''}`.trim();
    if (!raw) return `Charge ${index + 1}`;
    return this.normalizeChargeAllocationType(raw);
  }

  private resolveApplicationChargeAmount(item: ApplicationChargeApi): number {
    const amount = Number(item.amount);
    if (Number.isFinite(amount) && amount !== 0) return amount;
    return this.asNumber(item.rate_mwh) * this.asNumber(item.quantum_mwh);
  }

  private sanitizePercent(value: number | null | undefined): number {
    if (!Number.isFinite(Number(value))) return 0;
    return Math.min(Math.max(Number(value), 0), 100);
  }

  private cappedSellerPercent(value: number | null | undefined, buyerPercent: number): number {
    return Math.min(this.sanitizePercent(value), Math.max(100 - buyerPercent, 0));
  }

  private normalizeDate(value: string | null | undefined): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value.slice(0, 10);
    return date.toISOString().slice(0, 10);
  }

  private normalizeStatus(status: string | null | undefined): string {
    return (status || '').toLowerCase().trim();
  }

  private preloadBillConfigsForContracts(contracts: Contract[]): void {
    const loaIds = new Set<string>();
    contracts.forEach((contract) => {
      (contract.party_details || []).forEach((party) => {
        if (party.loa_id) loaIds.add(party.loa_id);
      });
    });
    loaIds.forEach((loaId) => this.ensureBillConfigsLoaded(loaId));
  }

  private preloadSchedulesForContracts(contracts: Contract[]): void {
    const loaIds = new Set<string>();
    contracts.forEach((contract) => {
      (contract.party_details || []).forEach((party) => {
        if (party.loa_id) loaIds.add(party.loa_id);
      });
    });
    loaIds.forEach((loaId) => this.ensureLoaSchedulesLoaded(loaId));
  }

  private ensureBillConfigsLoaded(loaId: string): void {
    if (!loaId) return;
    if (this.billConfigsByLoa()[loaId]) return;
    this.loaService.listBillConfigs(loaId).subscribe({
      next: (items) => {
        this.billConfigsByLoa.set({
          ...this.billConfigsByLoa(),
          [loaId]: items || [],
        });
      },
      error: () => {
        this.billConfigsByLoa.set({
          ...this.billConfigsByLoa(),
          [loaId]: [],
        });
      },
    });
  }

  private ensureLoaSchedulesLoaded(loaId: string): void {
    if (!loaId) return;
    if (this.loaSchedulesByLoa()[loaId]) return;
    this.loaService.listSchedules(loaId).subscribe({
      next: (items) => {
        this.loaSchedulesByLoa.set({
          ...this.loaSchedulesByLoa(),
          [loaId]: items || [],
        });
      },
      error: () => {
        this.loaSchedulesByLoa.set({
          ...this.loaSchedulesByLoa(),
          [loaId]: [],
        });
      },
    });
  }

  private parseDateOnly(value: string | null | undefined): Date | null {
    if (!value) return null;
    const normalized = this.normalizeDate(value);
    if (!normalized) return null;
    const [year, month, day] = normalized.split('-').map((part) => Number(part));
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }

  private isContractScheduledToday(contract: Contract): boolean {
    const today = this.todayAtMidnight();
    const contractStart = this.parseDateOnly(contract.period_from);
    const contractEnd = this.parseDateOnly(contract.period_to);

    if (!contractStart || !contractEnd || today < contractStart || today > contractEnd) return false;

    const loaIds = (contract.party_details || [])
      .map((party) => (party.loa_id || '').trim())
      .filter((loaId) => !!loaId);

    return loaIds.some((loaId) =>
      (this.loaSchedulesByLoa()[loaId] || []).some((schedule) => this.isScheduleActiveOnDate(schedule, today)),
    );
  }

  private isScheduleActiveOnDate(schedule: LoaSchedule, targetDate: Date): boolean {
    const scheduleStart = this.parseDateOnly(schedule.from_date);
    const scheduleEnd = this.parseDateOnly(schedule.to_date);
    if (!scheduleStart || !scheduleEnd) return false;
    return targetDate >= scheduleStart && targetDate <= scheduleEnd;
  }

  private todayAtMidnight(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  private resolveEstimatedContractUnits(
    contractStart: Date,
    contractEnd: Date,
    loa: Loa | null | undefined,
    fallbackQuantum: string | number | null | undefined,
  ): number {
    const schedules = loa?.id ? this.loaSchedulesByLoa()[loa.id] || [] : [];
    const scheduleUnits = schedules.reduce((total, schedule) => {
      const scheduleStart = this.parseDateOnly(schedule.from_date);
      const scheduleEnd = this.parseDateOnly(schedule.to_date);
      if (!scheduleStart || !scheduleEnd) return total;

      const overlapDays = this.overlapDayCount(contractStart, contractEnd, scheduleStart, scheduleEnd);
      if (!overlapDays) return total;

      const timeDifference = this.timeDifferenceHours(schedule.from_time, schedule.to_time);
      if (!timeDifference) return total;

      const quantum = this.firstFiniteNumber(schedule.quantum_mw, fallbackQuantum);
      if (quantum === null) return total;

      return total + overlapDays * timeDifference * quantum * 250;
    }, 0);

    if (scheduleUnits > 0) return scheduleUnits;

    const daysDifference = this.inclusiveDayCount(contractStart, contractEnd);
    const quantum = this.firstFiniteNumber(fallbackQuantum);
    if (!daysDifference || quantum === null) return 0;

    return daysDifference * 24 * quantum * 250;
  }

  private inclusiveDayCount(start: Date, end: Date): number {
    const diffMs = end.getTime() - start.getTime();
    if (diffMs < 0) return 0;
    return Math.floor(diffMs / (24 * 60 * 60 * 1000)) + 1;
  }

  private overlapDayCount(rangeStart: Date, rangeEnd: Date, scheduleStart: Date, scheduleEnd: Date): number {
    const start = Math.max(rangeStart.getTime(), scheduleStart.getTime());
    const end = Math.min(rangeEnd.getTime(), scheduleEnd.getTime());
    if (end < start) return 0;
    return Math.floor((end - start) / (24 * 60 * 60 * 1000)) + 1;
  }

  private timeDifferenceHours(fromTime: string | null | undefined, toTime: string | null | undefined): number {
    const fromMinutes = this.parseTimeToMinutes(fromTime);
    const toMinutes = this.parseTimeToMinutes(toTime);
    if (fromMinutes === null || toMinutes === null || toMinutes <= fromMinutes) return 0;
    return (toMinutes - fromMinutes) / 60;
  }

  private parseTimeToMinutes(value: string | null | undefined): number | null {
    if (!value) return null;
    const match = /^(\d{2}):(\d{2})$/.exec(value.trim());
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes) || minutes < 0 || minutes >= 60) return null;
    if (hours === 24 && minutes === 0) return 24 * 60;
    if (hours < 0 || hours > 23) return null;
    return hours * 60 + minutes;
  }

  private isNextCalendarMonth(target: Date, base: Date): boolean {
    const nextMonth = new Date(base.getFullYear(), base.getMonth() + 1, 1);
    return target.getFullYear() === nextMonth.getFullYear() && target.getMonth() === nextMonth.getMonth();
  }

  private mapCategoryKeyToFilter(categoryKey: Exclude<SummaryCategoryKey, 'bess'>): ContractCategoryFilter {
    switch (categoryKey) {
      case 'sale':
        return 'SALE-PURCHASE';
      case 'bank':
        return 'BANKING';
      case 'hybrid':
        return 'HYBRID';
    }
  }

  private parseCategoryFilter(value: string | null): ContractCategoryFilter {
    if (value === 'salePurchase') return 'SALE-PURCHASE';
    if (value === 'banking') return 'BANKING';
    if (value === 'hybrid') return 'HYBRID';
    return 'all';
  }

  private parseContractStatusFilter(value: string | null): string {
    return value === 'active' || value === 'expired' || value === 'starts_next_month' || value === 'upcoming' ? value : 'all';
  }

  private mapUnlinkedLoaLabelToCategory(categoryLabel: string): 'Buy' | 'Sell' | 'Banking' {
    const normalized = categoryLabel.trim().toLowerCase();
    if (normalized === 'sell') return 'Sell';
    if (normalized === 'banking') return 'Banking';
    return 'Buy';
  }

  private resolveEstimatedRate(loa: Loa | null | undefined, billConfigs: LoaBillConfig[]): number | null {
    const datedConfigs = [...billConfigs].sort((left, right) => (right.effective_date || '').localeCompare(left.effective_date || ''));
    const preferredConfigs = datedConfigs.filter((item) => (item.bill_type || '').trim().toUpperCase() !== 'OA');

    for (const config of preferredConfigs.length ? preferredConfigs : datedConfigs) {
      const directRate = this.firstFiniteNumber(config.rate, config.tmr_rate, config.mcp_rate);
      if (directRate !== null) return directRate;
    }

    return this.firstFiniteNumber(loa?.fixed_rate, loa?.mcp_value);
  }

  private resolveLoaTypeFromLoa(loa: Loa | null | undefined): string {
    const value = (loa?.category || '').trim().toLowerCase();
    if (!value) return '';
    if (value.includes('bank')) return 'Banking';
    if (value.includes('buy') || value.includes('purchase')) return 'Buy';
    if (value.includes('sell') || value.includes('sale')) return 'Sell';
    return loa?.category || '';
  }

  private resolveLoaKind(loaId: string | null | undefined): 'buy' | 'sell' | 'bank' | 'unknown' {
    if (!loaId) return 'unknown';
    const category = (this.loas().find((item) => item.id === loaId)?.category || '').toLowerCase();
    if (category.includes('sell') || category.includes('sale')) return 'sell';
    if (category.includes('buy') || category.includes('purchase')) return 'buy';
    if (category.includes('bank')) return 'bank';
    return 'unknown';
  }

  private resolveContractPartyKind(party: ContractPartyDetail | undefined): 'buy' | 'sell' | 'bank' | 'unknown' {
    if (!party) return 'unknown';
    const explicit = String(party.party_type || party.agreement_type || '').trim().toLowerCase();
    if (explicit.includes('sell') || explicit.includes('sale')) return 'sell';
    if (explicit.includes('buy') || explicit.includes('purchase')) return 'buy';
    if (explicit.includes('bank')) return 'bank';
    return this.resolveLoaKind(party.loa_id);
  }

  private contractQuantumMwhByKind(contract: Contract, kind: 'buy' | 'sell'): number | null {
    const contractStart = this.parseDateOnly(contract.period_from);
    const contractEnd = this.parseDateOnly(contract.period_to);
    if (!contractStart || !contractEnd) return null;

    const parties = contract.party_details || [];
    const matched = parties.filter((party) => this.resolveContractPartyKind(party) === kind);
    if (!matched.length) return null;

    let hasQuantum = false;
    const total = matched.reduce((sum, party) => {
      const loaId = String(party.loa_id || '').trim();
      const schedules = loaId ? this.loaSchedulesByLoa()[loaId] || [] : [];

      const scheduleMwh = schedules.reduce((scheduleTotal, schedule) => {
        const scheduleStart = this.parseDateOnly(schedule.from_date);
        const scheduleEnd = this.parseDateOnly(schedule.to_date);
        if (!scheduleStart || !scheduleEnd) return scheduleTotal;

        const overlapDays = this.overlapDayCount(contractStart, contractEnd, scheduleStart, scheduleEnd);
        if (!overlapDays) return scheduleTotal;

        const timeDifference = this.timeDifferenceHours(schedule.from_time, schedule.to_time);
        if (!timeDifference) return scheduleTotal;

        const quantum = this.firstFiniteNumber(schedule.quantum_mw, party.quantum);
        if (quantum === null) return scheduleTotal;

        hasQuantum = true;
        return scheduleTotal + overlapDays * timeDifference * quantum;
      }, 0);

      if (scheduleMwh > 0) return sum + scheduleMwh;

      const quantum = this.firstFiniteNumber(party.quantum);
      if (quantum === null) return sum;

      const daysDifference = this.inclusiveDayCount(contractStart, contractEnd);
      if (!daysDifference) return sum;

      hasQuantum = true;
      return sum + daysDifference * 24 * quantum;
    }, 0);

    return hasQuantum ? total : null;
  }

  private asNumber(value: string | number | null | undefined): number {
    if (value === null || value === undefined || value === '') return 0;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private firstFiniteNumber(...values: Array<string | number | null | undefined>): number | null {
    for (const value of values) {
      const parsed = this.asNumber(value);
      if (value !== null && value !== undefined && value !== '' && Number.isFinite(parsed) && parsed !== 0) {
        return parsed;
      }
    }
    return null;
  }

  private formatNumericValue(value: number): string {
    return Number.isFinite(value) ? value.toFixed(2).replace(/\.00$/, '') : '0';
  }

  private quantumDisplay(value: number | null): string {
    return value === null ? '-' : this.formatNumericValue(value);
  }

  private categoryMetrics(
    items: Contract[],
    options?: { includeBess?: boolean },
  ): Array<{ key: SummaryCategoryKey; label: string; count: number; mw: number }> {
    const nonBessItems = items.filter((item) => !this.isBessContract(item));
    const sales = nonBessItems.filter((item) => this.contractCategory(item) === 'SALE-PURCHASE').length;
    const banking = nonBessItems.filter((item) => this.contractCategory(item) === 'BANKING').length;
    const hybrid = nonBessItems.filter((item) => this.contractCategory(item) === 'HYBRID').length;
    const metrics: Array<{ key: SummaryCategoryKey; label: string; count: number; mw: number }> = [
      { key: 'sale', label: 'Sale-Purchase', count: sales, mw: sales * MW_PER_CONTRACT },
      { key: 'bank', label: 'Banking', count: banking, mw: banking * MW_PER_CONTRACT },
      { key: 'hybrid', label: 'Hybrid', count: hybrid, mw: hybrid * MW_PER_CONTRACT },
    ];

    if (options?.includeBess) {
      const bessCount = items.filter((item) => this.isBessContract(item)).length;
      metrics.push({ key: 'bess', label: 'BESS Contract', count: bessCount, mw: bessCount * MW_PER_CONTRACT });
    }

    return metrics;
  }
}
